import React, {type ReactNode, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useTopLayer} from '../../hooks/useTopLayer';
import styles from './ContextMenu.module.css';

/* ─────────────────────────────────────────────────────────────────────────
   Placement types
   ───────────────────────────────────────────────────────────────────────── */

export type ContextMenuSide = 'top' | 'bottom' | 'left' | 'right';
export type ContextMenuPlacement =
  | 'bottom-start' | 'bottom-end' | 'bottom'
  | 'top-start' | 'top-end' | 'top'
  | 'left-start' | 'left-end' | 'left'
  | 'right-start' | 'right-end' | 'right';

/* ─────────────────────────────────────────────────────────────────────────
   Position computation
   ───────────────────────────────────────────────────────────────────────── */

type Alignment = 'start' | 'end' | 'center';

const OPPOSITE: Record<ContextMenuSide, ContextMenuSide> = {
  top: 'bottom', bottom: 'top', left: 'right', right: 'left',
};

function parsePlacement(placement: ContextMenuPlacement): [ContextMenuSide, Alignment] {
  const [side, align] = placement.split('-') as [ContextMenuSide, Alignment | undefined];
  return [side, align ?? 'center'];
}

interface ComputedPosition {
  top: number;
  left: number;
  resolvedSide: ContextMenuSide;
}

function computePosition(
  anchorRect: DOMRect,
  popupWidth: number,
  popupHeight: number,
  placement: ContextMenuPlacement,
  offset: number = 4,
): ComputedPosition {
  const MARGIN = 6;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const [preferredSide, alignment] = parsePlacement(placement);

  function mainAxisPos(side: ContextMenuSide): number {
    switch (side) {
      case 'bottom':
        return anchorRect.bottom + offset;
      case 'top':
        return anchorRect.top - offset - popupHeight;
      case 'right':
        return anchorRect.right + offset;
      case 'left':
        return anchorRect.left - offset - popupWidth;
    }
  }

  function crossAxisPos(side: ContextMenuSide, align: Alignment): number {
    const isVertical = side === 'top' || side === 'bottom';
    if (isVertical) {
      if (align === 'start') {
        return anchorRect.left;
      }
      if (align === 'end') {
        return anchorRect.right - popupWidth;
      }
      /* center */
      return anchorRect.left + anchorRect.width / 2 - popupWidth / 2;
    } else {
      if (align === 'start') {
        return anchorRect.top;
      }
      if (align === 'end') {
        return anchorRect.bottom - popupHeight;
      }
      /* center */
      return anchorRect.top + anchorRect.height / 2 - popupHeight / 2;
    }
  }

  function sideOverflows(side: ContextMenuSide): boolean {
    switch (side) {
      case 'bottom':
        return anchorRect.bottom + offset + popupHeight > vh - MARGIN;
      case 'top':
        return anchorRect.top - offset - popupHeight < MARGIN;
      case 'right':
        return anchorRect.right + offset + popupWidth > vw - MARGIN;
      case 'left':
        return anchorRect.left - offset - popupWidth < MARGIN;
    }
  }

  let resolvedSide = preferredSide;
  if (sideOverflows(preferredSide)) {
    const flipped = OPPOSITE[preferredSide];
    if (!sideOverflows(flipped)) {
      resolvedSide = flipped;
    }
    // Both sides overflow — keep preferred and let clamping handle it
  }

  const isVertical = resolvedSide === 'top' || resolvedSide === 'bottom';
  let top: number;
  let left: number;

  if (isVertical) {
    top = mainAxisPos(resolvedSide);
    left = crossAxisPos(resolvedSide, alignment);
  } else {
    left = mainAxisPos(resolvedSide);
    top = crossAxisPos(resolvedSide, alignment);
  }

  // Clamp to viewport
  top = Math.max(MARGIN, Math.min(top, vh - popupHeight - MARGIN));
  left = Math.max(MARGIN, Math.min(left, vw - popupWidth - MARGIN));

  return {top, left, resolvedSide};
}

/* ─────────────────────────────────────────────────────────────────────────
   ContextMenu (portal)
   ───────────────────────────────────────────────────────────────────────── */

export interface ContextMenuProps {
  anchor: HTMLElement;
  placement?: ContextMenuPlacement;
  onClose: () => void;
  children: ReactNode;
}

export function ContextMenu({anchor, placement = 'bottom-end', onClose, children}: ContextMenuProps) {
  const popupRef = useRef<HTMLDivElement>(null);
  const [popupElement, setPopupElement] = useState<HTMLDivElement | null>(null);

  // Portal target: when a native <dialog> is open via showModal(), everything OUTSIDE
  // that dialog's DOM subtree is made inert by the browser (unclickable, unfocusable,
  // hidden from a11y) - regardless of paint order. So if the anchor lives inside a
  // dialog, we must portal INTO that dialog to remain interactive, not just visually
  // on top of it.
  const portalTarget: Element = anchor.closest('dialog') ?? document.body;

  // Promotes the menu into the browser's top layer, so it always paints above the
  // dialog even though it's nested inside it (DOM nesting and top-layer paint order
  // are independent) - and so it still works correctly when there's no enclosing
  // dialog at all.
  useTopLayer(popupElement);

  const setRef = (el: HTMLDivElement | null) => {
    popupRef.current = el;
    setPopupElement(el);
  };

  // Popup dimensions — updated by ResizeObserver so computePosition always has real values
  const [popupSize, setPopupSize] = useState({width: 0, height: 0});

  // Resolved position + side (side drives the open animation direction)
  const [pos, setPos] = useState<ComputedPosition | null>(null);

  // ── Measure popup via ResizeObserver ──────────────────────────────────
  useLayoutEffect(
    () => {
      const el = popupRef.current;
      if (!el) {
        return undefined;
      }
      const ro = new ResizeObserver(entries => {
        const {width, height} = entries[0].contentRect;
        setPopupSize({width, height});
      });
      ro.observe(el);
      return () => ro.disconnect();
    },
    []
  );

  // ── Recompute position whenever anchor moves or popup size changes ────
  useLayoutEffect(
    () => {
      if (popupSize.width === 0 && popupSize.height === 0) {
        return undefined;
      }

      function update() {
        const rect = anchor.getBoundingClientRect();
        setPos(computePosition(rect, popupSize.width, popupSize.height, placement));
      }

      update();

      // Reposition on any scroll (capture phase catches all scrollable ancestors)
      window.addEventListener('scroll', update, {capture: true, passive: true});
      window.addEventListener('resize', update, {passive: true});

      return () => {
        window.removeEventListener('scroll', update, {capture: true});
        window.removeEventListener('resize', update);
      };
    },
    [anchor, placement, popupSize]
  );

  // ── Hide when anchor is scrolled out of view ──────────────────────────
  useEffect(
    () => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) {
            onClose();
          }
        },
        {threshold: 0},
      );
      observer.observe(anchor);
      return () => observer.disconnect();
    },
    [anchor, onClose]
  );

  // ── Close on outside mousedown ────────────────────────────────────────
  useEffect(
    () => {
      const handler = (e: MouseEvent) => {
        if (
          popupRef.current &&
          !popupRef.current.contains(e.target as Node) &&
          !anchor.contains(e.target as Node)
        ) {
          onClose();
        }
      };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    },
    [anchor, onClose]
  );

  // Determine animation class from resolved side (available after first measure)
  const side = pos?.resolvedSide ?? parsePlacement(placement)[0];
  const animClassMap: Record<ContextMenuSide, string> = {
    bottom: styles.openFrom_bottom,
    top: styles.openFrom_top,
    left: styles.openFrom_left,
    right: styles.openFrom_right,
  };
  const animClass = animClassMap[side];

  const style = pos
    ? {top: pos.top, left: pos.left, visibility: 'visible' as const}
    : {top: -9999, left: -9999, visibility: 'hidden' as const};

  return createPortal(
    <div ref={setRef} className={`${styles.component} ${animClass ?? ''}`} style={style}>
      {children}
    </div>,
    portalTarget,
  );
}